/**
 * Utility functions for date validation in form fields
 */

type DateConstraint = {
  fromField?: boolean
  isToday?: boolean
  value?: string | Date
}

type DateValidation = {
  minDate?: DateConstraint
  maxDate?: DateConstraint
}

// Helper function to extract fieldName from pattern like ${fieldName}
export const extractFieldName = (expression: string): string | null => {
  const matches = expression.match(/\${([^}]+)}/)
  return matches ? matches[1] : null
}

// Helper function to get constraint date values
export const getConstraintDates = (
  dateValidation: DateValidation | undefined,
  extractFieldFn: (expr: string) => string | null,
  watchFieldFn: (name: string | string[]) => any
): { minDate?: Date; maxDate?: Date } => {
  const result: { minDate?: Date; maxDate?: Date } = {}

  if (!dateValidation) return result

  // Handle minDate
  if (dateValidation.minDate) {
    const { fromField, isToday, value } = dateValidation.minDate

    if (isToday) {
      result.minDate = new Date()
    } else if (fromField && typeof value === 'string') {
      const fieldName = extractFieldFn(value)
      if (fieldName) {
        const fieldValue = watchFieldFn(fieldName)
        if (fieldValue) {
          result.minDate = new Date(fieldValue)
        }
      }
    } else if (value) {
      result.minDate = new Date(value)
    }
  }

  // Handle maxDate
  if (dateValidation.maxDate) {
    const { fromField, isToday, value } = dateValidation.maxDate

    if (isToday) {
      result.maxDate = new Date()
    } else if (fromField && typeof value === 'string') {
      const fieldName = extractFieldFn(value)
      if (fieldName) {
        const fieldValue = watchFieldFn(fieldName)
        if (fieldValue) {
          result.maxDate = new Date(fieldValue)
        }
      }
    } else if (value) {
      result.maxDate = new Date(value)
    }
  }

  return result
}

// Gets a Date object without time components (set to 00:00:00)
export const getDateWithoutTime = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

// Check if two dates are the same day (ignoring time)
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

// Validation result with error type information
export type ValidationResult = {
  isValid: boolean
  errorType?: 'min' | 'max' | 'same_as_min' | 'general'
}

// Validates a datetime value against min/max constraints
export const validateDateTime = (
  value: Date,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  isMinDateFromField: boolean
): ValidationResult => {
  let result: ValidationResult = { isValid: true }

  // Check if value is less than minDate
  if (minDate && value < minDate) {
    result = { isValid: false, errorType: 'min' }
  }
  // If minDate is from another field, also check if dates are exactly the same
  else if (minDate && isMinDateFromField && value.getTime() === minDate.getTime()) {
    result = { isValid: false, errorType: 'same_as_min' }
  }

  // Check if value is greater than maxDate
  if (maxDate && value > maxDate) {
    result = { isValid: false, errorType: 'max' }
  }

  return result
}

// Validates a date value against min/max constraints (ignoring time)
export const validateDateOnly = (
  value: Date,
  minDate: Date | undefined,
  maxDate: Date | undefined,
  isMinDateFromField: boolean
): ValidationResult => {
  let result: ValidationResult = { isValid: true }

  // Normalize dates to remove time components
  const valueDate = getDateWithoutTime(value)

  if (minDate) {
    const minDateOnly = getDateWithoutTime(minDate)

    // Check if current date is less than minDate
    if (valueDate < minDateOnly) {
      result = { isValid: false, errorType: 'min' }
    }
    // If minDate is from another field, also check if dates are exactly the same
    else if (isMinDateFromField && isSameDay(valueDate, minDateOnly)) {
      result = { isValid: false, errorType: 'same_as_min' }
    }
  }

  // Check if current date is greater than maxDate
  if (maxDate) {
    const maxDateOnly = getDateWithoutTime(maxDate)
    if (valueDate > maxDateOnly) {
      result = { isValid: false, errorType: 'max' }
    }
  }

  return result
}

// Generate appropriate error message based on field type and validation context
export const getDateValidationErrorMessage = (
  isDateTimeLocal: boolean,
  errorType: 'min' | 'max' | 'same_as_min' | 'general' = 'general',
  fieldLabel?: string
): string => {
  const dateTimeLabel = isDateTimeLocal ? 'date and time' : 'date'

  // If it's the specific case of a date being the same as minDate from another field
  if (errorType === 'same_as_min') {
    const targetField = fieldLabel || 'Start Date'
    return isDateTimeLocal ? `End Date and Time must be after ${targetField}` : `End Date must be after ${targetField}`
  }

  // More general error messages for other cases
  if (errorType === 'min') {
    return `Please select a ${dateTimeLabel} after the minimum allowed`
  }

  if (errorType === 'max') {
    return `Please select a ${dateTimeLabel} before the maximum allowed`
  }

  // Default generic message
  return `Please select a ${dateTimeLabel} between the allowed range`
}
